import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FileText, Download, CheckCircle2, Filter, Loader2, Search, MessageCircle, AlertTriangle, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

const ESTADO_META = {
  cobrada: { label: "Cobrada", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  pendiente_cobro: { label: "Pendiente cobro", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  emitida: { label: "Emitida", cls: "bg-blue-500/20 text-blue-300 border-blue-500/40" },
  anulada: { label: "Anulada", cls: "bg-rose-500/20 text-rose-300 border-rose-500/40" },
};

export default function Facturas() {
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [marcandoId, setMarcandoId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/facturas");
      setFacturas(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando facturas");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtradas = useMemo(() => {
    const q = busqueda.toLowerCase().trim();
    return facturas.filter((f) => {
      if (filtroEstado && f.estado !== filtroEstado) return false;
      if (!q) return true;
      const snap = f.cliente_snapshot || {};
      return (
        (f.numero || "").toLowerCase().includes(q) ||
        (snap.razon_social || "").toLowerCase().includes(q) ||
        (snap.cif || "").toLowerCase().includes(q)
      );
    });
  }, [facturas, filtroEstado, busqueda]);

  const totales = useMemo(() => {
    const t = { total: 0, cobrado: 0, pendiente: 0, n: 0, n_pendientes: 0 };
    filtradas.forEach((f) => {
      const total = Number(f.totales?.total || 0);
      const pagado = Number(f.importe_pagado || 0);
      if (f.estado === "anulada") return;
      t.total += total;
      t.cobrado += pagado;
      t.pendiente += Math.max(total - pagado, 0);
      t.n += 1;
      if (f.estado === "pendiente_cobro") t.n_pendientes += 1;
    });
    return t;
  }, [filtradas]);

  const marcarCobrada = async (f) => {
    if (!window.confirm(`¿Marcar ${f.numero} como COBRADA manualmente?`)) return;
    setMarcandoId(f.id);
    try {
      await api.post(`/facturas/${f.id}/marcar-cobrada`);
      toast.success(`${f.numero} marcada como cobrada`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setMarcandoId(null); }
  };

  const descargar = (fid, numero) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("access_token");
    fetch(`${base}/api/facturas/${fid}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${numero}.pdf`; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Error descargando PDF"));
  };

  const enviarLinkCobro = async (f) => {
    try {
      const { data: r } = await api.post(`/presupuestos/${f.presupuesto_id}/whatsapp-cobro`);
      window.open(r.url, "_blank", "noopener,noreferrer");
      toast.success(`Link de cobro listo · Pendiente ${r.importes.pendiente.toFixed(2)}€`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando link");
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="facturas-page">
      <PageHeader title="Facturas" subtitle="Emitidas, cobradas y pendientes · Numeración FAC-YYYY-XXXX" icon={FileText} />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Facturas (filtradas)" value={totales.n} accent="text-zinc-100" />
        <KPI label="Total emitido" value={eur(totales.total)} accent="text-zinc-100" />
        <KPI label="Cobrado" value={eur(totales.cobrado)} accent="text-emerald-300" testid="kpi-cobrado" />
        <KPI label={`Pendiente · ${totales.n_pendientes} factura${totales.n_pendientes!==1?'s':''}`}
             value={eur(totales.pendiente)} accent="text-amber-300" testid="kpi-pendiente" />
      </div>

      {/* Filtros */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            placeholder="Buscar por nº, cliente o CIF…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="bg-zinc-900 border-zinc-800 pl-9"
            data-testid="input-buscar-factura"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {["", "pendiente_cobro", "cobrada", "emitida", "anulada"].map((s) => (
            <Button key={s||"all"} size="sm"
              variant={filtroEstado === s ? "default" : "outline"}
              onClick={() => setFiltroEstado(s)}
              data-testid={`filtro-${s||"todas"}`}
              className={filtroEstado === s ? "bg-amber-500 hover:bg-amber-600 text-zinc-900 font-semibold" : "border-zinc-700"}>
              {s ? (ESTADO_META[s]?.label || s) : "Todas"}
            </Button>
          ))}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mr-2" />Cargando…</div>
        ) : filtradas.length === 0 ? (
          <div className="text-center py-16 text-zinc-500 text-sm">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No hay facturas {filtroEstado ? `en estado "${ESTADO_META[filtroEstado]?.label}"` : busqueda ? `que coincidan con "${busqueda}"` : "emitidas todavía"}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase text-zinc-500 border-b border-zinc-800">
                  <th className="px-3 py-2.5">Nº</th>
                  <th className="px-3 py-2.5">Cliente</th>
                  <th className="px-3 py-2.5">Fecha</th>
                  <th className="px-3 py-2.5 text-right">Total</th>
                  <th className="px-3 py-2.5 text-right">Pagado</th>
                  <th className="px-3 py-2.5 text-right">Pendiente</th>
                  <th className="px-3 py-2.5">Estado</th>
                  <th className="px-3 py-2.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((f) => {
                  const total = Number(f.totales?.total || 0);
                  const pagado = Number(f.importe_pagado || 0);
                  const pendiente = Math.max(total - pagado, 0);
                  const meta = ESTADO_META[f.estado] || { label: f.estado, cls: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40" };
                  const snap = f.cliente_snapshot || {};
                  const diasEmi = (() => {
                    try {
                      const d = new Date(f.fecha_emision);
                      return Math.floor((Date.now() - d.getTime()) / 86400000);
                    } catch { return null; }
                  })();
                  const vencida = f.estado === "pendiente_cobro" && diasEmi !== null && diasEmi > 7;
                  return (
                    <tr key={f.id} className="border-b border-zinc-800/60 hover:bg-zinc-800/30" data-testid={`fila-factura-${f.numero}`}>
                      <td className="px-3 py-2.5 font-mono text-amber-300 font-semibold">
                        <Link to={`/presupuestos/${f.presupuesto_id}`} className="hover:underline" title="Abrir presupuesto origen">
                          {f.numero}
                        </Link>
                      </td>
                      <td className="px-3 py-2.5 text-zinc-200">
                        <div className="font-medium">{snap.razon_social || "—"}</div>
                        <div className="text-[10px] text-zinc-500">{snap.cif || ""}</div>
                      </td>
                      <td className="px-3 py-2.5 text-zinc-400 text-xs">
                        {f.fecha_emision}
                        {vencida && (
                          <div className="text-[10px] text-rose-300 mt-0.5 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> {diasEmi}d sin cobrar
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right font-semibold text-zinc-100">{eur(total)}</td>
                      <td className="px-3 py-2.5 text-right text-emerald-300">{eur(pagado)}</td>
                      <td className={`px-3 py-2.5 text-right font-semibold ${pendiente > 0.01 ? "text-amber-300" : "text-emerald-300"}`}>{eur(pendiente)}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded border ${meta.cls}`}>{meta.label}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => descargar(f.id, f.numero)}
                            data-testid={`btn-descargar-${f.numero}`}
                            className="h-7 px-2 text-zinc-400 hover:text-amber-300" title="Descargar PDF">
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          {f.estado === "pendiente_cobro" && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => enviarLinkCobro(f)}
                                data-testid={`btn-enviar-cobro-${f.numero}`}
                                className="h-7 px-2 text-zinc-400 hover:text-emerald-300" title="Enviar link de pago WhatsApp">
                                <MessageCircle className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost"
                                disabled={marcandoId === f.id}
                                onClick={() => marcarCobrada(f)}
                                data-testid={`btn-marcar-cobrada-${f.numero}`}
                                className="h-7 px-2 text-zinc-400 hover:text-emerald-300" title="Marcar cobrada (manual)">
                                {marcandoId === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                              </Button>
                            </>
                          )}
                          <Link to={`/presupuestos/${f.presupuesto_id}`}
                            className="h-7 px-2 inline-flex items-center text-zinc-500 hover:text-amber-300"
                            title="Abrir presupuesto origen" data-testid={`btn-abrir-presu-${f.numero}`}>
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, accent, testid }) {
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent || "text-zinc-100"}`}>{value}</div>
    </div>
  );
}
